export const DEMO_PROJECT_TITLE = 'MIDNIGHT CIRCUIT';

export const DEMO_SCRIPT = `TITLE: MIDNIGHT CIRCUIT
GENRE: Action / Sports Drama

INT. UNDERGROUND RACE ARENA - NIGHT

Cold blue light cuts through the smoke. LENA, 24, watches the starting grid from the shadows. Her eyes lock on ALEX KANE, the undefeated rider in a black racing suit.

The crowd erupts as Alex raises one hand. Engines answer like thunder.

EXT. CITY CIRCUIT - CONTINUOUS

Alex launches his superbike into the neon rain. Lena follows on a vintage blue scooter, taking a narrow service lane as the faster machines close in behind her.

She twists the throttle. Fire bursts from the exhaust and the city becomes a tunnel of light.

At the final corner, a lizard crosses the scorched curb. Lena swerves. Alex looks back, realizing what she risked to stay in the race.

INT. WINNER'S GARAGE - LATER

The crowd cheers as Alex applauds Lena. Between two red curtains, her pink helmet sits alone beneath a white spotlight.

EXT. CITY HOSPITAL - PRE-DAWN

Alex runs toward the emergency entrance. The race is over, but their story has just begun.`;

const image = (number) => `/demo-assets/IMG_${number}.jpg`;

const rawShots = [
  ['极特写', '缓慢推进', '冷蓝侧光', '紧张、专注', '眼睛占据画面中心，浅景深', '汗珠、赛场灯光', 'Lena watches the starting grid; cold blue light reflects in her eyes.', 'Lena', image(9160)],
  ['侧面特写', '横向微移', '蓝紫轮廓光', '克制、警觉', '人物侧脸三分构图', '潮湿发丝、霓虹虚化', 'Lena turns toward the roar of the engines.', 'Lena', image(9161)],
  ['正面近景', '固定镜头', '冷色顶光', '下定决心', '对称居中构图', '黑色连帽衫、赛场背景', 'She pulls up her hood and steps out of the shadows.', 'Lena', image(9162)],
  ['中景', '低机位上摇', '蓝紫舞台光', '自信、从容', '英雄式低机位构图', '赛车服、起跑舞台', 'Alex Kane raises his hand as the arena erupts.', 'Alex', image(9163)],
  ['全景', '低机位跟拍', '紫色烟雾光', '爆发、速度感', '赛车从画面左下冲出', '黑色机车、赛道烟雾', 'Alex launches from the grid on his black superbike.', 'Alex', image(9164)],
  ['远景', '后方跟拍', '湿地霓虹反光', '追逐、压迫', '两车形成前后纵深', '雨夜赛道、蓝色踏板车', 'Lena takes the service lane while Alex closes in.', 'Lena & Alex', image(9165)],
  ['全景', '侧前方追拍', '冷白路灯', '勇敢、孤注一掷', '广角动势构图', '粉色头盔、蓝色踏板车', 'Lena leans forward and pushes the vintage scooter to its limit.', 'Lena', image(9166)],
  ['人物特写', '快速推近', '冷蓝硬光', '震惊、担忧', '正面中心构图', '伤痕、皮夹克', 'Her father watches the race, fear breaking through his expression.', 'Father', image(9167)],
  ['大全景', '高速后跟', '蓝白光轨', '失控边缘的兴奋', '消失点居中，强透视', '火焰尾气、城市光轨', 'Flame bursts from the exhaust as the city becomes a tunnel of light.', 'Lena', image(9168)],
  ['微距特写', '固定后拉焦', '橙蓝对比光', '危险迫近', '前景主体，背景火焰虚化', '蜥蜴、焦黑路肩、轮胎墙', 'A lizard freezes on the scorched curb at the final corner.', '—', image(9169)],
  ['人物特写', '手持微晃', '蓝紫轮廓光', '错愕、醒悟', '正面紧凑构图', '汗水、赛车服', 'Alex looks back and understands what Lena risked.', 'Alex', image(9170)],
  ['中近景', '缓慢拉远', '舞台柔光', '敬意、释然', '人物居中，观众环绕', '欢呼观众、赛车服', 'Alex applauds Lena as the crowd rises behind him.', 'Alex', image(9171)],
  ['大全景', '缓慢推进', '单点顶光', '悬念、静默', '红幕对称框景', '粉色头盔、红色幕布', 'Her pink helmet waits beneath a single white spotlight.', '—', image(9172)],
  ['远景', '后方稳定跟拍', '医院冷白光', '急切、希望', '台阶引导线通向入口', '医院标志、玻璃门', 'Before dawn, Alex runs toward the emergency entrance.', 'Alex', image(9173)],
];

export const DEMO_SHOTS = rawShots.map((shot, index) => ({
  id: `demo-${index + 1}`,
  number: index + 1,
  title: shot[0],
  shotSize: shot[0],
  cameraMove: shot[1],
  lighting: shot[2],
  emotion: shot[3],
  composition: shot[4],
  environment: shot[5],
  desc: shot[6],
  character: shot[7],
  img: shot[8],
  largeImg: shot[8],
  duration: index === 0 || index === 12 ? 3 : 2,
  status: 'ready',
}));

export const DEMO_VIDEO_URL = '/demo-assets/413.MP4';

export const cloneDemoShots = () => DEMO_SHOTS.map((shot) => ({ ...shot }));

export function demoDuration(milliseconds) {
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('fastDemo') === '1') {
    return Math.min(milliseconds, 700);
  }
  return milliseconds;
}

export function parseScriptLocally(script) {
  const lines = script
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line && !/^(TITLE|GENRE):/i.test(line));
  const beats = lines.filter((line) => !/^(INT\.|EXT\.)/i.test(line));
  const source = beats.length ? beats : [script.trim()];

  return source.slice(0, 8).map((desc, index) => ({
    id: `shot-${Date.now()}-${index}`,
    number: index + 1,
    title: ['全景', '中景', '近景', '特写'][index % 4],
    shotSize: ['全景', '中景', '近景', '特写'][index % 4],
    cameraMove: ['固定镜头', '缓慢推进', '横向跟拍', '手持跟拍'][index % 4],
    lighting: '电影感自然光',
    emotion: '根据剧本语境',
    composition: '三分法电影构图',
    environment: '由剧本场景自动提取',
    desc,
    character: '自动识别',
    duration: 3,
  }));
}
